import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import Styles from './GroupedBarChart.module.scss'

interface DataPoint {
  range: string
  series: Array<{
    name: string
    value: number
    color: string
  }>
}

interface TooltipData {
  range: string
  name: string
  value: number
  x: number
  y: number
}

interface GroupedBarChartProps {
  data: DataPoint[]
}

const GroupedBarChart: React.FC<GroupedBarChartProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)

  const height = 350

  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        setWidth(wrapperRef.current.clientWidth)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (width === 0 || !data.length) return

    const margin = { top: 10, right: 20, bottom: 30, left: 20 }
    const padding = { inner: 10, barGap: 7 }

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove()

    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    // Create scales
    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.range))
      .range([0, innerWidth])
      .padding(0)

    const numBars = 3 // Male, Female, Did not share
    const barWidth = (xScale.bandwidth() - 2 * padding.inner - padding.barGap * (numBars - 1)) / numBars

    // Create array of x positions for the bars
    const barPositions = ['Male', 'Female', 'Did not share'].map(
      (_, i) => padding.inner + i * (barWidth + padding.barGap),
    )

    // Create custom scale for bar positions
    const xSubScale = (name: string) => {
      const index = ['Male', 'Female', 'Did not share'].indexOf(name)
      return barPositions[index]
    }

    const yScale = d3.scaleLinear().domain([0, 20]).range([innerHeight, 0])

    // Create SVG
    const svg = d3.select(svgRef.current).attr('width', width).attr('height', height)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    // Add border to the chart area
    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('stroke', 'var(--dark-4)')
      .attr('stroke-width', 1)

    // Add vertical grid lines (between groups)
    data.forEach((_, i) => {
      if (i < data.length - 1) {
        const xPos = (xScale(data[i].range) as number) + xScale.bandwidth()
        g.append('line')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', 'var(--dark-4)')
          .attr('stroke-width', 1)
      }
    })

    // Add grouped bars
    data.forEach((d) => {
      const group = g.append('g').attr('transform', `translate(${xScale(d.range)},0)`)

      d.series.forEach((s) => {
        group
          .append('rect')
          .attr('x', xSubScale(s.name))
          .attr('y', yScale(s.value))
          .attr('width', barWidth)
          .attr('height', innerHeight - yScale(s.value))
          .attr('fill', s.color)
          .attr('rx', 2)
          .style('cursor', 'pointer')
          .on('mouseover', (event) => {
            const groupTransform = (event.target.parentNode as SVGGElement).getAttribute('transform')
            const groupX = groupTransform ? parseFloat(groupTransform.split('(')[1]) : 0

            // Calculate position relative to the wrapper
            const barCenterX = groupX + xSubScale(s.name) + barWidth / 2

            setTooltipData({
              range: d.range,
              name: s.name,
              value: s.value,
              x: barCenterX + margin.left,
              y: margin.top + yScale(s.value),
            })
          })
          .on('mouseout', () => setTooltipData(null))
      })
    })

    // Add x-axis with proper spacing
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick line').remove())
      .call((g) =>
        g
          .selectAll('.tick text')
          .style('font-size', '12px')
          .style('color', 'var(--dark-60)')
          .attr('text-anchor', 'middle'),
      )
  }, [data, width])

  return (
    <div ref={wrapperRef} className={Styles.container}>
      <svg ref={svgRef} aria-label="Grouped bar chart" />
      {tooltipData && (
        <div
          className={Styles.tooltip}
          style={{
            left: tooltipData.x,
            top: tooltipData.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className={Styles.tooltipHeader}>{tooltipData.range}</div>
          <div className={Styles.tooltipContent}>
            <span>{tooltipData.name}</span>
            <span>{tooltipData.value.toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupedBarChart
